/**
 * 5897 — GMV dos eventos já em venda: por data da venda (padrão) ou por data
 * do evento, valor total ou de face, com o mesmo período do ano anterior.
 * Eventos que ainda vão abrir não entram (são imprevisíveis por natureza).
 */
import { FC, ReactElement, useRef, useState } from 'react'
import styled from 'styled-components'
import { GmvHorizonte, PainelPrevisoes, PrevisaoDiaria, TopGmv } from '../../../types/previsoes'
import { BD, Card, PageSubtitle, PageTitle, Table } from '../styles'
import {
  BarraReferencia,
  COR,
  CartaoTabela,
  ComPainel,
  Entenda,
  Kpi,
  Kpis,
  Nota,
  Rolagem,
  Segmentado,
  brl,
  pct,
} from './comum'
import { Dica } from './Evento'

type Visao = 'venda' | 'evento'
type Valor = 'total' | 'face'
const HORIZONTES = ['1', '3', '7', '14', '30', '60', '90', '120']
const DIA = 86400000

const Controles = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`
const Tops = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 420px), 1fr));
  gap: 16px;
  margin-bottom: 16px;
`

const ddmm = (d: Date): string =>
  `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`
/** AAAA-MM-DD de hoje no fuso de Brasília (o snapshot é gravado com essa data de referência). */
const hojeSaoPaulo = (): string => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date())
const variacao = (a: number, b?: number): string => {
  if (!b) return ''
  const d = a / b - 1
  return `${d >= 0 ? '▲' : '▼'} ${Math.abs(Math.round(d * 100))}% vs ano anterior`
}

const TabelaTop: FC<{ titulo: string; linhas?: TopGmv[]; coluna: string; face: boolean }> = ({
  titulo,
  linhas,
  coluna,
  face,
}) => (
  <CartaoTabela>
    <div className="titulo">
      <h3>{titulo}</h3>
    </div>
    <Rolagem>
      <Table>
        <thead>
          <tr>
            <th>Evento</th>
            <th className="n">Data</th>
            <th className="n">{coluna}</th>
          </tr>
        </thead>
        <tbody>
          {(linhas ?? []).map((r) => (
            <tr key={`${r.nome}|${r.data}`} style={{ cursor: 'default' }}>
              <td style={{ fontWeight: 500 }}>
                {r.nome}
                {r.ja_em_venda === false && <small>abriu depois deste ponto</small>}
              </td>
              <td className="n">{r.data}</td>
              <td className="n">{brl(face ? r.face : r.gmv)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Rolagem>
  </CartaoTabela>
)

/** GMV diário: últimos 120 dias realizados + linha do ano anterior (364 dias antes) até o fim do horizonte. */
const GraficoDiario: FC<{ painel: PainelPrevisoes; horizonte: number; face: boolean }> = ({ painel, horizonte, face }) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const caixaRef = useRef<HTMLDivElement>(null)
  const [hover, setHover] = useState<{ i: number; left: number } | null>(null)
  const D = (face ? painel.face_diario : painel.gmv_diario) ?? {}
  const T = new Date(`${painel.referencia}T00:00:00Z`).getTime()
  const chave = (t: number): string => new Date(t).toISOString().slice(0, 10)
  const prevPorDia = new Map<string, PrevisaoDiaria>()
  ;(painel.gmv_previsao_diaria ?? []).forEach((q) => prevPorDia.set(q.d, q))
  const n0 = 120
  const pts: Array<{ t: number; atual: number | null; anterior: number | null; prev: number | null; lo: number | null; hi: number | null }> = []
  for (let i = -n0; i < horizonte; i++) {
    const t = T + i * DIA
    const q = i >= 0 ? prevPorDia.get(chave(t)) : undefined
    pts.push({
      t,
      atual: i < 0 ? D[chave(t)] ?? null : null,
      anterior: D[chave(t - 364 * DIA)] ?? null,
      prev: q ? (face ? q.face_p50 : q.p50) : null,
      lo: q ? (face ? q.face_p10 : q.p10) : null,
      hi: q ? (face ? q.face_p90 : q.p90) : null,
    })
  }
  const W = 900
  const H = 260
  const M = { l: 64, r: 14, t: 12, b: 28 }
  const valores = pts.flatMap((p) => [p.atual, p.anterior, p.hi]).filter((v): v is number => v !== null)
  const ymax = Math.max(...valores, 1) * 1.05
  const x = (i: number): number => M.l + ((W - M.l - M.r) * i) / (pts.length - 1)
  const y = (v: number): number => H - M.b - ((H - M.t - M.b) * v) / ymax
  const linha = (f: (p: typeof pts[number]) => number | null): string => {
    let d = ''
    let caneta = false
    pts.forEach((p, i) => {
      const v = f(p)
      if (v === null) {
        caneta = false
        return
      }
      d += `${caneta ? 'L' : 'M'}${x(i)},${y(v)}`
      caneta = true
    })
    return d
  }
  // faixa de 80% da previsão dia a dia (polígono p90 → p10)
  const comPrev = pts.map((q, i) => ({ q, i })).filter(({ q }) => q.lo !== null && q.hi !== null)
  const faixa =
    comPrev.length > 1
      ? `${comPrev.map(({ q, i }, j) => `${j === 0 ? 'M' : 'L'}${x(i)},${y(q.hi as number)}`).join('')}${comPrev
          .slice()
          .reverse()
          .map(({ q, i }) => `L${x(i)},${y(q.lo as number)}`)
          .join('')}Z`
      : ''
  const passo = Math.max(7, Math.round(pts.length / 8))
  const mover = (ev: React.PointerEvent): void => {
    const svg = svgRef.current
    const caixa = caixaRef.current
    if (!svg || !caixa) return
    const bb = svg.getBoundingClientRect()
    const px = ((ev.clientX - bb.left) * W) / bb.width
    const i = Math.max(0, Math.min(pts.length - 1, Math.round(((px - M.l) / (W - M.l - M.r)) * (pts.length - 1))))
    setHover({ i, left: Math.min(ev.clientX - caixa.getBoundingClientRect().left + 12, caixa.clientWidth - 200) })
  }
  const p = hover ? pts[hover.i] : null

  return (
    <Card ref={caixaRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>{face ? 'Valor de face' : 'Valor total'} diário (por data da venda)</h3>
        <span style={{ fontSize: 12, color: BD.grayText }}>
          últimos 120 dias e {horizonte === 1 ? 'o dia previsto' : `os ${horizonte} dias previstos`}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: BD.grayText, margin: '4px 0' }}>
        <span>
          <i style={{ display: 'inline-block', width: 14, height: 3, borderRadius: 2, marginRight: 6, verticalAlign: 'middle', background: COR.serie }} />
          Realizado
        </span>
        <span>
          <i style={{ display: 'inline-block', width: 14, height: 3, borderRadius: 2, marginRight: 6, verticalAlign: 'middle', background: COR.anterior }} />
          Mesmo período do ano anterior (mesmo dia da semana, 52 semanas antes)
        </span>
        {comPrev.length > 0 && (
          <span>
            <i style={{ display: 'inline-block', width: 14, height: 3, borderRadius: 2, marginRight: 6, verticalAlign: 'middle', background: COR.serie, opacity: 0.55 }} />
            Previsto dia a dia (faixa de 80% sombreada, até 14 dias)
          </span>
        )}
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="GMV diário realizado e ano anterior"
        style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((t) => (
          <g key={t}>
            <line x1={M.l} x2={W - M.r} y1={y(ymax * t)} y2={y(ymax * t)} stroke={COR.grade} strokeDasharray="3 3" />
            <text x={M.l - 6} y={y(ymax * t) + 4} textAnchor="end" fontSize={11} fill={COR.eixo}>
              {brl(ymax * t)}
            </text>
          </g>
        ))}
        {faixa !== '' && <path d={faixa} fill={COR.serie} opacity={0.12} />}
        <path d={linha((q) => q.anterior)} fill="none" stroke={COR.anterior} strokeWidth={1.4} strokeDasharray="4 3" />
        <path d={linha((q) => q.atual)} fill="none" stroke={COR.serie} strokeWidth={1.8} />
        <path d={linha((q) => q.prev)} fill="none" stroke={COR.serie} strokeWidth={1.6} strokeDasharray="2 3" opacity={0.7} />
        <line x1={x(n0 - 0.5)} x2={x(n0 - 0.5)} y1={M.t} y2={H - M.b} stroke={BD.grayText} strokeDasharray="2 3" />
        <text x={x(n0 - 0.5) + 6} y={M.t + 10} fontSize={11} fill={COR.eixo}>
          hoje
        </text>
        {pts.map((q, i) =>
          i % passo === 0 ? (
            <text key={q.t} x={x(i)} y={H - 8} textAnchor="middle" fontSize={11} fill={COR.eixo}>
              {ddmm(new Date(q.t))}
            </text>
          ) : null,
        )}
        {hover && <line x1={x(hover.i)} x2={x(hover.i)} y1={M.t} y2={H - M.b} stroke={BD.grayText} strokeDasharray="2 3" />}
        <rect
          x={M.l}
          y={M.t}
          width={W - M.l - M.r}
          height={H - M.t - M.b}
          fill="transparent"
          onPointerMove={mover}
          onPointerLeave={() => setHover(null)}
        />
      </svg>
      {hover && p && (
        <Dica style={{ left: hover.left }}>
          <div className="quando">
            {new Date(p.t).toLocaleDateString('pt-BR', {
              weekday: 'short',
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              timeZone: 'UTC',
            })}
          </div>
          {p.atual !== null && (
            <div>
              Realizado: <b>{brl(p.atual)}</b>
            </div>
          )}
          {p.prev !== null && (
            <div>
              Previsto: <b>{brl(p.prev)}</b> <span style={{ color: BD.grayText }}>({brl(p.lo as number)} – {brl(p.hi as number)})</span>
            </div>
          )}
          <div>
            Ano anterior: <b>{p.anterior !== null ? brl(p.anterior) : '—'}</b>
          </div>
        </Dica>
      )}
      <Entenda
        paragrafos={[
          'Linha azul: GMV realizado por dia da compra (total pago nos pedidos do site + valor e taxa dos ingressos do PDV e de comissários). Pedidos cancelados ou devolvidos não entram.',
          'Linha cinza: o mesmo dia da semana 52 semanas antes, para comparar a sazonalidade. À direita de "hoje" ela mostra como foram os próximos dias no ano passado. Picos isolados costumam ser aberturas de venda de grandes eventos.',
          'Por data do evento, "ano anterior · mesmo ponto" soma só os eventos que já estavam em venda naquele dia do ano passado, que é a comparação justa com a previsão. "Período inteiro" inclui os que abriram depois.',
        ]}
      />
    </Card>
  )
}

const Conteudo: FC<{ painel: PainelPrevisoes; referencias: string[] }> = ({ painel, referencias }) => {
  const [visao, setVisao] = useState<Visao>('venda')
  const [hz, setHz] = useState('30')
  const [valor, setValor] = useState<Valor>('total')
  const horizontes = HORIZONTES.filter((k) => painel.gmv[k])
  const o: GmvHorizonte | undefined = painel.gmv[hz]
  if (!o) return <PageSubtitle>Sem dados de GMV neste snapshot.</PageSubtitle>
  const F = valor === 'face'
  const fx = o.face
  const q = painel.qualidade?.gmv?.[hz]
  const T = new Date(`${painel.referencia}T00:00:00Z`)
  const periodo = hz === '1' ? ddmm(T) : `${ddmm(T)} a ${ddmm(new Date(T.getTime() + (Number(hz) - 1) * DIA))}`
  // As vendas vão até a meia-noite da referência: o primeiro dia previsto é o próprio dia da
  // referência, que é "hoje" quando o snapshot é o do dia (e não "o próximo dia").
  const ehHoje = painel.referencia === hojeSaoPaulo()
  const rotuloUmDia = ehHoje ? 'Hoje' : ddmm(T)
  const noHorizonte =
    hz === '1'
      ? ehHoje
        ? `hoje (${periodo})`
        : `em ${periodo}`
      : ehHoje
        ? `nos ${hz} dias a partir de hoje (${periodo})`
        : `de ${periodo}`
  const textoValor = F
    ? 'Valor de face dos ingressos (sem taxas, seguro e juros).'
    : 'Valor total pago pelo comprador (ingresso + conveniência + processamento + seguro + juros).'

  let corpo: ReactElement
  if (visao === 'evento') {
    const e = F
      ? { p50: fx.evento_p50, p10: fx.evento_p10, p90: fx.evento_p90, ja_vendido: fx.evento_ja_vendido, eventos: o.por_data_evento.eventos }
      : o.por_data_evento
    const lyAbertos = F ? o.ano_anterior_face_abertos : o.ano_anterior_evento_abertos
    const lyTotal = F ? o.ano_anterior_face_total : o.ano_anterior_evento_total
    corpo = (
      <>
        <Nota>
          {textoValor} Valor final dos eventos já em venda que acontecem {noHorizonte} (já vendido + o que falta
          vender). Não é a venda do período: para isso, use "Vendas no período".
          {q?.evento_so_abertos ? ` No teste, o erro típico neste horizonte foi ${pct(q.evento_so_abertos.MdAPE)}.` : ''}
        </Nota>
        <Kpis>
          <Kpi rotulo={`Previsão · ${periodo}`} valor={brl(e.p50)} dica={`faixa ${brl(e.p10)} – ${brl(e.p90)}`} />
          <Kpi rotulo="Já vendido" valor={brl(e.ja_vendido)} dica={`${e.eventos} eventos com data no período`} />
          <Kpi
            rotulo="Ano anterior · mesmo ponto"
            valor={lyAbertos !== undefined ? brl(lyAbertos) : '—'}
            dica={`${
              o.ano_anterior_eventos_abertos_n !== undefined
                ? `${o.ano_anterior_eventos_abertos_n} eventos já em venda naquele dia · `
                : ''
            }${variacao(e.p50, lyAbertos)}`}
          />
          <Kpi
            rotulo="Ano anterior · período inteiro"
            valor={lyTotal !== undefined ? brl(lyTotal) : '—'}
            dica="todos os eventos que aconteceram no período, inclusive os que abriram depois"
          />
        </Kpis>
        <Tops>
          <TabelaTop
            titulo={`Maiores eventos previstos · ${periodo}`}
            linhas={o.top}
            coluna={F ? 'Face prevista' : 'Total previsto'}
            face={F}
          />
          <TabelaTop
            titulo="Maiores eventos do mesmo período do ano anterior"
            linhas={o.ano_anterior_top}
            coluna={F ? 'Face final' : 'Total final'}
            face={F}
          />
        </Tops>
      </>
    )
  } else {
    const v = F ? { p50: fx.venda_p50, p10: fx.venda_p10, p90: fx.venda_p90 } : o.por_data_venda
    const realizado = F ? fx.ultimos_h_dias : o.ultimos_h_dias
    const anterior = F ? fx.ano_anterior_venda : o.ano_anterior_venda
    const erroVenda = F ? fx.venda_erro_tipico : o.venda_erro_tipico
    corpo = (
      <>
        {o.venda_metodo === 'blend' ? (
          <Nota $tom="atencao">
            {textoValor} Quanto a plataforma deve vender {noHorizonte}, todos os eventos. Até 14 dias a previsão combina a
            soma das projeções por evento (corrigida pelo viés) com o ritmo dos últimos 7 dias: no teste fora da amostra
            essa combinação foi a mais estável
            {erroVenda ? ` (erro típico de ${pct(erroVenda)})` : ''}. Um dia isolado varia muito; para planejar, use a
            visão por evento.
          </Nota>
        ) : (
          <Nota $tom="atencao">
            {textoValor} Quanto os eventos já em venda devem vender {noHorizonte}, somando todos os eventos, mesmo os que
            acontecem depois. Valores corrigidos pelo viés medido no teste, com faixa calibrada fora da amostra
            {erroVenda ? ` (erro típico de ${pct(erroVenda)})` : q?.so_abertos ? ` (erro típico de ${pct(q.so_abertos.MdAPE)})` : ''}.
            A comparação com o ano anterior inclui todos os eventos, inclusive os que ainda iam abrir.
          </Nota>
        )}
        <Kpis>
          <Kpi rotulo={`Previsão · ${periodo}`} valor={brl(v.p50)} dica={`faixa ${brl(v.p10)} – ${brl(v.p90)}`} />
          <Kpi
            rotulo={hz === '1' ? 'Ontem (realizado)' : `Últimos ${hz} dias (realizado)`}
            valor={brl(realizado)}
            dica={o.venda_metodo === 'blend' ? 'todos os eventos · o ritmo recente entra na previsão' : 'todos os eventos'}
          />
          <Kpi
            rotulo="Mesmo período do ano anterior"
            valor={brl(anterior)}
            dica={`realizado, todos os eventos · ${variacao(v.p50, anterior)}`}
          />
        </Kpis>
        {(painel.gmv_previsao_diaria ?? []).length > 0 && (
          <CartaoTabela style={{ marginBottom: 16 }}>
            <div className="titulo">
              <h3>Previsão dia a dia</h3>
              <div style={{ fontSize: 12.5, color: BD.grayText }}>
                Derivada dos totais de 1, 3, 7 e 14 dias (combinação eventos + ritmo recente), repartidos por igual entre
                os dias de cada trecho; a faixa e o erro são os do trecho. A tabela fecha com os KPIs.
              </div>
            </div>
            <Rolagem>
              <Table>
                <thead>
                  <tr>
                    <th>Dia</th>
                    <th className="n">Previsto</th>
                    <th className="n">Faixa 80%</th>
                    <th className="n">Erro típico</th>
                  </tr>
                </thead>
                <tbody>
                  {(painel.gmv_previsao_diaria ?? []).map((q) => (
                    <tr key={q.d} style={{ cursor: 'default' }}>
                      <td style={{ fontWeight: 500 }}>
                        {new Date(`${q.d}T00:00:00Z`).toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'UTC' })}
                      </td>
                      <td className="n">{brl(F ? q.face_p50 : q.p50)}</td>
                      <td className="n">
                        {brl(F ? q.face_p10 : q.p10)} – {brl(F ? q.face_p90 : q.p90)}
                      </td>
                      <td className="n">{pct(q.erro)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Rolagem>
          </CartaoTabela>
        )}
      </>
    )
  }

  return (
    <>
      <PageTitle>GMV dos eventos em venda</PageTitle>
      <PageSubtitle>
        Valor vendido considerando só os eventos que já estão em venda, comparado ao mesmo período do ano anterior.
        Escolha entre o valor total pago pelo comprador e o valor de face dos ingressos.
      </PageSubtitle>
      <BarraReferencia painel={painel} referencias={referencias} />
      <Controles>
        <Segmentado<Visao>
          rotulo="Visão"
          valor={visao}
          onChange={setVisao}
          opcoes={[
            ['venda', 'Vendas no período'],
            ['evento', 'Eventos que acontecem no período'],
          ]}
        />
        <Segmentado<string>
          rotulo="Horizonte"
          valor={hz}
          onChange={setHz}
          opcoes={horizontes.map((k) => [k, k === '1' ? rotuloUmDia : `${k} dias`] as [string, string])}
        />
        <Segmentado<Valor>
          rotulo="Valor"
          valor={valor}
          onChange={setValor}
          opcoes={[
            ['total', 'Valor total'],
            ['face', 'Valor de face'],
          ]}
        />
      </Controles>
      {corpo}
      <GraficoDiario painel={painel} horizonte={Number(hz)} face={F} />
    </>
  )
}

export const PrevisoesGmv: FC = () => (
  <ComPainel>{(painel, referencias) => <Conteudo painel={painel} referencias={referencias} />}</ComPainel>
)
